import { useMemo, useState } from 'react';
import type {
  ConversationState,
  EveDynamicToolPart,
  EveMessage,
  EveMessageInputRequest,
  EveMessagePart,
} from 'eve/react';
// Type-only: these aren't re-exported from `eve/react`. A type import erases at
// build, so it's safe (unlike a runtime `eve/client` import, which pulls
// Node-only modules; see the note in lib/conversations.ts).
import type {
  InputResolutionOutcome,
  InputResponse,
  MessageStreamEvent,
} from 'eve/client';
import { ActivityFeed, type ActivityStep } from './ActivityFeed';
import { useChatActions } from './ChatActions';
import { renderBlock, TextBlock } from './primitives';

/**
 * Renders one assistant turn from its eve message parts.
 *
 * The agent emits prose as `text` parts and rich blocks as `render_*` tool
 * calls (`dynamic-tool` parts). Each `render_*` tool has identity `execute` and
 * `inputSchema = blockProps.<Block>`, so its `output` (or `input`, before the
 * output lands) IS the catalog block's props — we pass it straight to the
 * matching primitive in `primitives.tsx`.
 *
 * The agent's *work* — its `reasoning` and non-render tool calls (running a
 * Trino query, shell commands, loading reference data) — is not the answer, so
 * a run of consecutive work parts collapses into a single `<ActivityFeed>`
 * (a live "what it's doing" line + timer while streaming, a `Worked for Ns`
 * summary once settled) instead of one dim row per call. `streaming` marks the
 * turn as still in flight so the last work run keeps its live indicator even
 * after its final tool has settled.
 */
export function EveAssistant({
  message,
  streaming,
  requests,
}: {
  message: EveMessage;
  streaming?: boolean;
  /** Where each HITL request stands, see {@link sessionRequests}. */
  requests?: Requests;
}) {
  const chunks = useMemo(() => groupParts(message.parts), [message.parts]);
  return (
    <div className="he-fadein" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {chunks.map((chunk, i) =>
        chunk.kind === 'activity' ? (
          <ActivityFeed key={chunk.key} steps={chunk.steps} live={streaming && i === chunks.length - 1} />
        ) : (
          <ContentBlock key={chunk.key} part={chunk.part} requests={requests} />
        ),
      )}
    </div>
  );
}

/**
 * A text or reasoning part's text, as a string whatever arrived. The type says
 * string, but the reducer copies it off the wire, and an eve server older than
 * the client completes a step with no message as `message: null`, which lands
 * here as a `null` text. One `.trim()` on that blanks the whole app, not just
 * this turn.
 */
function partText(part: { text?: unknown }): string {
  return typeof part.text === 'string' ? part.text : '';
}

type Chunk =
  | { kind: 'activity'; key: string; steps: ActivityStep[] }
  | { kind: 'content'; key: string; part: EveMessagePart };

/**
 * Split parts into ordered chunks: runs of consecutive *work* parts (reasoning
 * + non-render tools) each become one activity chunk; each renderable part
 * (non-empty prose, `render_*` block) is its own content chunk. `step-start`,
 * empty text/reasoning and the `no_reply` tool are transparent: they never
 * split a work run (empty text deltas arrive between tool calls).
 */
function groupParts(parts: readonly EveMessagePart[]): Chunk[] {
  const chunks: Chunk[] = [];
  // Steps that closed the turn through `no_reply` without presenting. A step is
  // presenting when it made at least one call besides `no_reply` and every such
  // call is a `render_*` call: the Slack channel's `isDelivery` rule, so text
  // beside a `no_reply` shows here exactly when Slack posts it. Text in any
  // other closing step is narration about not replying.
  const closingSteps = new Set<number>();
  const stepCalls = new Map<number, string[]>();
  let step = 0;
  for (const part of parts) {
    if (part.type === 'step-start') step += 1;
    else if (part.type === 'dynamic-tool' && part.toolName === NO_REPLY_TOOL) closingSteps.add(step);
    else if (part.type === 'dynamic-tool') stepCalls.set(step, [...(stepCalls.get(step) ?? []), part.toolName]);
  }
  const presenting = (s: number) => {
    const calls = stepCalls.get(s) ?? [];
    return calls.length > 0 && calls.every((name) => name.startsWith('render_'));
  };
  const quietSteps = new Set([...closingSteps].filter((s) => !presenting(s)));
  step = 0;
  let run: ActivityStep[] = [];
  // Where the open run started, so its chunk key is fixed once the run opens.
  // Keying on the first STEP would move when a part ahead of it changes chunk —
  // a render tool settling to `output-error` joins the run and becomes its new
  // first step — and a changed key remounts the feed, losing the step timings
  // it measured and the reader's expanded state.
  let runStart = 0;
  const flush = () => {
    if (run.length) {
      chunks.push({ kind: 'activity', key: `act-${runStart}`, steps: run });
      run = [];
    }
  };
  const push = (step: ActivityStep, i: number) => {
    if (!run.length) runStart = i;
    run.push(step);
  };

  parts.forEach((part, i) => {
    if (part.type === 'step-start') step += 1;
    if (part.type === 'reasoning' && (partText(part).trim() || part.state === 'streaming')) {
      push({ key: `r${i}`, kind: 'reasoning', text: partText(part), active: part.state === 'streaming' }, i);
      return;
    }
    // A HITL input request (the `ask_question` clarifying question, or a tool
    // approval) is answerable UI, not agent work — give it its own content
    // chunk so the user can respond. Without this it falls into the activity
    // run below and renders as an inert, forever-spinning step, and the turn
    // (parked awaiting the answer) never resumes.
    if (part.type === 'dynamic-tool' && part.toolMetadata?.eve?.inputRequest) {
      flush();
      // Whether it is still answerable is read from the session, not the part
      // (see sessionRequests), so a restored thread does not re-offer controls
      // over a request the session moved past.
      chunks.push({ kind: 'content', key: part.toolCallId, part });
      return;
    }
    // `no_reply` is how the agent ends a turn it already answered without
    // answering again. It is the absence of a step, so it is not shown as one.
    if (part.type === 'dynamic-tool' && part.toolName === NO_REPLY_TOOL) return;
    if (part.type === 'dynamic-tool' && !isRenderTool(part)) {
      push(toolStep(part), i);
      return;
    }
    if (part.type === 'text' && partText(part).trim() && !quietSteps.has(step)) {
      flush();
      chunks.push({ kind: 'content', key: `t${i}`, part });
      return;
    }
    if (part.type === 'dynamic-tool' && isRenderTool(part)) {
      // A render tool that THREW has no block to be, so it belongs with the
      // agent's other work rather than in the answer. Its `errorText` is eve's
      // message to the MODEL — for the non-identity tools a `/workspace/out/`
      // path and an instruction to write the file first — and it stays out of
      // the DOM entirely; eve's logs are where that text is read. What the
      // reader gets is a failed step in the feed, the same place every other
      // tool's failure lands.
      if (part.state === 'output-error') {
        push(toolStep(part), i);
        return;
      }
      flush();
      chunks.push({ kind: 'content', key: part.toolCallId, part });
      return;
    }
    // step-start, empty text, settled empty reasoning: no content, never flush.
  });
  flush();
  return chunks;
}

/**
 * Whether an assistant message has anything <EveAssistant> would render — prose,
 * a rendered block, or agent work. Derived from `groupParts` so there is one
 * source of truth for "visible": ChatView uses it to keep the loading skeleton
 * up until the first visible part lands (avoiding a blank gap after the message
 * object appears with only a `step-start`).
 */
export function hasVisibleContent(message: EveMessage): boolean {
  return groupParts(message.parts).length > 0;
}

/** A content chunk: streamed prose, a HITL input request, or a rendered block. */
function ContentBlock({ part, requests }: { part: EveMessagePart; requests?: Requests }) {
  if (part.type === 'text') return <TextBlock text={partText(part)} />;
  if (part.type === 'dynamic-tool') {
    const request = part.toolMetadata?.eve?.inputRequest;
    if (request)
      return (
        <InputRequest
          part={part}
          request={request}
          response={part.toolMetadata?.eve?.inputResponse}
          state={requestStateOf(requests?.states, request.requestId)}
        />
      );
    return <ToolBlock part={part} />;
  }
  return null;
}

/** How the agent ends a turn it already answered, without answering again. */
const NO_REPLY_TOOL = 'no_reply';

/**
 * Where a HITL request stands, as this SPA renders it: `open` offers controls,
 * `sent` is an answer submitted and not yet settled by eve, and `closed` is a
 * request eve settled (`outcome` says how) or, in a session recorded before eve
 * settled requests, one the session moved past (no outcome).
 */
type RequestState =
  | { status: 'open' }
  | { status: 'sent'; response?: InputResponse }
  | { status: 'closed'; outcome?: string; response?: InputResponse };

const OPEN: RequestState = { status: 'open' };

/** The eve release that first emits `input.resolved`, closing each request it settles. */
const INPUT_RESOLVED_SINCE = [0, 39, 1] as const;

/**
 * Whether the session was recorded by an eve that predates `input.resolved`,
 * read from the version its `session.started` names. Such a session never
 * settles a request, and an answer there started a new turn.
 */
function predatesInputResolved(events: readonly MessageStreamEvent[]): boolean {
  for (const e of events) {
    if (e.type !== 'session.started') continue;
    const match = /^(\d+)\.(\d+)\.(\d+)/.exec(e.data.runtime?.eveVersion ?? '');
    if (!match) return false;
    const version = match.slice(1, 4).map(Number);
    for (let i = 0; i < version.length; i++) {
      if (version[i] !== INPUT_RESOLVED_SINCE[i]) return version[i]! < INPUT_RESOLVED_SINCE[i];
    }
    return false;
  }
  return false;
}

/**
 * Where every request in the session stands.
 *
 * eve's conversation state is the authority: `inputs[id].status` is `open`,
 * `responded` (the answer this tab submitted, before eve confirms it) or
 * `settled`, with the outcome eve recorded. A request can stay open across
 * turns, so a later turn alone does not close one. Only in a session recorded
 * before eve settled requests (see `predatesInputResolved`), where answering
 * started a new turn, is a request closed once a `turn.started` for a different
 * turn follows it; it closes without an outcome, since that session never said
 * whether it was answered.
 */
function requestStates(
  inputs: ConversationState['inputs'],
  events: readonly MessageStreamEvent[],
): ReadonlyMap<string, RequestState> {
  const superseded = new Set<string>();
  if (predatesInputResolved(events)) {
    const seen: { requestId: string; turnId: string }[] = [];
    for (const e of events) {
      if (e.type === 'input.requested') {
        for (const r of e.data.requests) seen.push({ requestId: r.requestId, turnId: e.data.turnId });
      } else if (e.type === 'turn.started') {
        for (const r of seen) if (r.turnId !== e.data.turnId) superseded.add(r.requestId);
      }
    }
  }
  const states = new Map<string, RequestState>();
  for (const [id, input] of Object.entries(inputs)) {
    if (input.status === 'settled') states.set(id, { status: 'closed', outcome: input.outcome, response: input.response });
    else if (superseded.has(id)) states.set(id, { status: 'closed', response: input.response });
    else if (input.status === 'responded') states.set(id, { status: 'sent', response: input.response });
    else states.set(id, OPEN);
  }
  return states;
}

function requestStateOf(states: ReadonlyMap<string, RequestState> | undefined, requestId: string): RequestState {
  return states?.get(requestId) ?? OPEN;
}

/**
 * The session's HITL requests, with the one definition of "open" that decides
 * what renders controls, what `answer()` accepts, and whether a turn is waiting
 * on the person (`isOpen`, `openInTurn`).
 */
export interface Requests {
  readonly states: ReadonlyMap<string, RequestState>;
  isOpen(requestId: string): boolean;
  /** Whether the given turn has an open request of its own. */
  openInTurn(turnId: string): boolean;
}

export function sessionRequests(inputs: ConversationState['inputs'], events: readonly MessageStreamEvent[]): Requests {
  const states = requestStates(inputs, events);
  const isOpen = (requestId: string) => requestStateOf(states, requestId).status === 'open';
  return {
    states,
    isOpen,
    openInTurn: (turnId) =>
      Object.values(inputs).some((input) => input.turnId === turnId && isOpen(input.request.requestId)),
  };
}

/**
 * How a settled request reads, by eve's outcome. `taken` is whether eve acted
 * on an answer, so the chosen option is shown; otherwise the note stands alone.
 * Keyed by eve's own outcome type, so an outcome eve adds fails to typecheck here.
 */
const OUTCOME_NOTE: Record<InputResolutionOutcome, { taken: boolean; note: string }> = {
  answered: { taken: true, note: 'Answered.' },
  approved: { taken: true, note: 'Approved.' },
  denied: { taken: true, note: 'Denied.' },
  cancelled: { taken: false, note: 'Withdrawn: the agent stopped waiting for an answer.' },
  ignored: { taken: false, note: 'Skipped: the conversation moved on.' },
  invalid: { taken: false, note: 'Not accepted.' },
};

/**
 * A HITL input request the agent raised and holds the turn on. Two shapes,
 * both answered inline (never a popup) and both resuming the turn on submit:
 *  - a clarifying question (`ask_question`): prompt + options and/or a freeform
 *    box;
 *  - a tool approval (`kind: 'tool-approval'`): shows the action being
 *    approved (the gated tool + its arguments, from the part) above approve/deny.
 * Controls show only while the request is open. Once answered it collapses to
 * the chosen answer, and a request eve closed without acting on an answer says
 * how it ended.
 */
function InputRequest({
  part,
  request,
  response,
  state,
}: {
  part: EveDynamicToolPart;
  request: EveMessageInputRequest;
  response?: InputResponse;
  state: RequestState;
}) {
  const { onAnswer } = useChatActions();
  // The request's own `kind` classifies it; a session-limit continuation is a
  // question with its own options, not an approval of a tool call.
  const isApproval = request.kind === 'tool-approval';
  const hasOptions = (request.options?.length ?? 0) > 0;
  // Offer a text answer when freeform is allowed OR there's nothing to click —
  // a bare prompt (no options) is inherently a text question and would
  // otherwise render with no way to respond, stranding the turn.
  const showFreeform = request.allowFreeform || !hasOptions;
  // The response comes from eve's conversation state (the answer submitted, or
  // the one eve's `input.resolved` recorded), else from the part. A session
  // recorded before eve emitted that event has none to show, so there a closed
  // request reads as answered but not with what.
  const given = state.status === 'open' ? undefined : (state.response ?? response);
  const outcome = state.status === 'closed' ? state.outcome : undefined;
  const note =
    outcome !== undefined && Object.hasOwn(OUTCOME_NOTE, outcome)
      ? OUTCOME_NOTE[outcome as InputResolutionOutcome]
      : undefined;
  const chosen =
    given && (note === undefined || note.taken)
      ? (request.options?.find((o) => o.id === given.optionId)?.label ?? given.text)
      : undefined;
  // Closed with no outcome is a session that never recorded whether it was
  // answered, so the note claims neither way.
  const closedNote = state.status === 'closed' && chosen === undefined ? (note?.note ?? 'No longer open.') : undefined;

  return (
    <div style={{ background: 'var(--bg-1)', borderRadius: 14, padding: '16px 18px' }}>
      {isApproval && <ApprovalAction part={part} />}
      <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--fg-1)', lineHeight: 1.45 }}>{request.prompt}</div>
      {chosen !== undefined ? (
        <div style={{ marginTop: 10, fontSize: 13.5, color: 'var(--fg-2)' }}>
          <span style={{ color: 'var(--he-purple)', marginRight: 6 }}>✓</span>
          {chosen}
        </div>
      ) : closedNote !== undefined ? (
        <div style={{ marginTop: 10, fontSize: 13.5, color: 'var(--fg-3)' }}>{closedNote}</div>
      ) : state.status !== 'open' ? null : (
        <>
          {hasOptions && (
            <Options options={request.options!} onPick={(id) => onAnswer(request.requestId, id)} />
          )}
          {showFreeform && <FreeformAnswer onSubmit={(text) => onAnswer(request.requestId, undefined, text)} />}
        </>
      )}
    </div>
  );
}

/**
 * The action a `confirmation` request is asking the user to approve: the gated
 * tool (friendly name) and a one-line preview of its arguments, so the user
 * sees WHAT they're authorizing, not just an anonymous approve/deny.
 */
function ApprovalAction({ part }: { part: EveDynamicToolPart }) {
  const detail = toolDetail(part);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--fg-3)', marginBottom: 6 }}>
        Approval needed
      </div>
      <div style={{ fontSize: 13.5, color: 'var(--fg-2)' }}>
        <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{toolLabel(part)}</span>
        {detail ? <span style={{ color: 'var(--fg-3)' }}> — {detail}</span> : null}
      </div>
    </div>
  );
}

/**
 * Answer options for a HITL request. A flat pill row when the options are bare
 * labels; a stacked list (label + description) as soon as any option carries a
 * `description`, so the extra context is visible, not hidden behind a hover.
 */
function Options({
  options,
  onPick,
}: {
  options: NonNullable<EveMessageInputRequest['options']>;
  onPick: (id: string) => void;
}) {
  const stacked = options.some((o) => o.description);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        flexWrap: stacked ? 'nowrap' : 'wrap',
        gap: 8,
        marginTop: 12,
      }}
    >
      {options.map((o) => {
        const primary = o.style === 'primary';
        const danger = o.style === 'danger';
        const fg = danger ? 'var(--he-red)' : primary ? '#fff' : 'var(--fg-1)';
        return (
          <button
            key={o.id}
            onClick={() => onPick(o.id)}
            title={stacked ? undefined : o.description}
            className="he-press he-focusable"
            style={{
              textAlign: 'left',
              padding: stacked ? '11px 15px' : '9px 16px',
              borderRadius: stacked ? 12 : 999,
              border: primary ? 'none' : '1px solid var(--border-1)',
              background: primary ? 'var(--he-purple)' : 'var(--bg-1)',
              color: fg,
              fontSize: 13.5,
              fontWeight: primary ? 600 : 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              if (!primary) e.currentTarget.style.borderColor = 'var(--he-purple)';
            }}
            onMouseLeave={(e) => {
              if (!primary) e.currentTarget.style.borderColor = 'var(--border-1)';
            }}
          >
            <span style={{ fontWeight: stacked ? 600 : primary ? 600 : 500 }}>{o.label}</span>
            {stacked && o.description && (
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 400,
                  marginTop: 2,
                  color: primary ? 'rgba(255,255,255,.82)' : 'var(--fg-3)',
                }}
              >
                {o.description}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Freeform text answer for a HITL question that permits one. */
function FreeformAnswer({ onSubmit }: { onSubmit: (text: string) => void }) {
  const [text, setText] = useState('');
  const submit = () => {
    const t = text.trim();
    if (t) onSubmit(t);
  };
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Type an answer…"
        className="he-focusable"
        style={{
          flex: 1,
          height: 38,
          borderRadius: 10,
          border: '1px solid var(--border-1)',
          background: 'var(--bg-2)',
          padding: '0 12px',
          fontSize: 13.5,
          fontFamily: 'inherit',
          color: 'var(--fg-1)',
        }}
      />
      <button
        onClick={submit}
        disabled={!text.trim()}
        className="he-press he-focusable"
        style={{
          padding: '0 16px',
          borderRadius: 10,
          border: 'none',
          background: text.trim() ? 'var(--he-purple)' : 'var(--bg-3)',
          color: text.trim() ? '#fff' : 'var(--fg-3)',
          fontSize: 13.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: text.trim() ? 'pointer' : 'default',
        }}
      >
        Send
      </button>
    </div>
  );
}

/**
 * A tool call as an activity step (in flight → done/error): every non-render
 * tool, plus a render tool that failed and so has no block to be.
 *
 * A render tool's `input` is the block's own props, and the feed prefers a
 * detail over the label — so passing one through would put a page title next to
 * the warning marker in place of the work. The suppression lives here rather
 * than in `toolDetail`, whose other caller (`ApprovalAction`) shows the reader
 * what they are authorizing and needs the arguments.
 */
function toolStep(part: EveDynamicToolPart): ActivityStep {
  const state =
    part.state === 'output-error'
      ? 'error'
      : part.state === 'output-available' || part.state === 'output-denied'
        ? 'done'
        : 'active';
  const detail = isRenderTool(part) ? undefined : toolDetail(part);
  return { key: part.toolCallId, kind: 'tool', label: toolLabel(part), detail, state };
}

/**
 * The one test for "this part is a render tool", so the routing, the label and
 * the detail cannot disagree about which parts they cover. Keyed on `toolName`:
 * `toolMetadata.eve.name` is the SUBAGENT name for a subagent call and the
 * skill's name for a skill load, so it identifies a different thing.
 */
function isRenderTool(part: EveDynamicToolPart): boolean {
  return part.toolName.startsWith('render_');
}

/**
 * What a render tool was drawing, in the reader's words. The block kinds are
 * catalog jargon (`radial`, `split`, `follow`), so they are mapped rather than
 * spelled out; anything unmapped is "the answer", which is true of every block.
 */
const RENDER_NOUN: Record<string, string> = {
  page: 'page',
  download: 'file',
  table: 'table',
  map: 'map',
  hotspot: 'hotspot card',
  metrics: 'figures',
  area: 'chart',
  bars: 'chart',
  split: 'chart',
  pie: 'chart',
  radial: 'chart',
};

/**
 * Friendly label for a tool call in the activity feed: every non-render tool,
 * plus a render tool that failed (which has no block to be).
 */
function toolLabel(part: EveDynamicToolPart): string {
  // The `kind` checks come first because they classify what a call IS, and a
  // skill or subagent may be named anything at all, including `render_*` or a
  // name containing `query`.
  if (part.toolMetadata?.eve?.kind === 'load-skill') return 'Loading reference data';
  if (part.toolMetadata?.eve?.kind === 'subagent-call') return 'Delegating a subtask';
  if (part.toolName === SEARCH_TOOL) return 'Searching tools';
  if (isRenderTool(part)) return `Preparing the ${RENDER_NOUN[part.toolName.slice('render_'.length)] ?? 'answer'}`;
  // A connection tool's part is named by its qualified name, `<conn>__<tool>`
  // (`helium__trino_query`), with the tool's own arguments as its input.
  if (/trino|query/i.test(part.toolName)) return 'Running query';
  // Strip the `<conn>__` connection prefix and humanize the rest.
  const base = part.toolName.replace(/^[^_]+__/, '').replace(/_+/g, ' ').trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Working';
}

/** eve's catalog search, how the model finds a connection tool before calling it. */
const SEARCH_TOOL = 'eve__search';

/**
 * A short human hint pulled from a tool call's input — a Bash `description` or
 * `command`, a SQL preview, a catalog search's `query` — so the running line
 * says what it's doing, not just `Bash`. Falls back to nothing (the label
 * carries it).
 */
function toolDetail(part: EveDynamicToolPart): string | undefined {
  const input = part.input;
  if (!input || typeof input !== 'object') return undefined;
  const rec = input as Record<string, unknown>;
  for (const field of ['description', 'title', 'command', 'query', 'sql', 'prompt'] as const) {
    const v = rec[field];
    if (typeof v === 'string' && v.trim()) {
      const oneLine = v.trim().replace(/\s+/g, ' ');
      return oneLine.length > 80 ? `${oneLine.slice(0, 79)}…` : oneLine;
    }
  }
  return undefined;
}

/** The validated block props for a render_* part, once its arguments are complete. */
/**
 * Whether a part is a page that actually published. `render_page` is output-only
 * (its input is a sandbox path, not the block props), so a part still streaming,
 * awaiting approval, or settled as `output-error` carries no URL and is not a
 * page anyone can open. Exported so nothing has to re-derive that from the tool
 * name alone: a scan matching the name in any state claims pages that don't exist.
 */
export function isPublishedPage(part: EveMessagePart): boolean {
  return part.type === 'dynamic-tool' && part.toolName === 'render_page' && part.state === 'output-available';
}

function blockProps(part: EveDynamicToolPart): unknown {
  // `render_download` and `render_page` are the NON-identity render tools: their
  // input is a sandbox path (`{ path, ... }`), not the block props — only their
  // output (the published URL) is renderable. Rendering the input-available state
  // would hand <DownloadCard>/<PageCard> a props object with no `url` and crash
  // the turn, so gate them to output. Every other render tool is identity, so
  // input === the block props.
  if (part.toolName === 'render_download' || part.toolName === 'render_page') {
    return part.state === 'output-available' ? part.output : undefined;
  }
  switch (part.state) {
    case 'output-available':
      return part.output;
    case 'input-available':
      return part.input;
    // `input-streaming` (args still arriving), errors and approval states have
    // no complete props to render yet.
    default:
      return undefined;
  }
}

function ToolBlock({ part }: { part: EveDynamicToolPart }) {
  // groupParts routes a failed render tool into the activity feed, so a part
  // reaching here is never `output-error`. `blockProps` returns undefined for
  // that state regardless, so it renders nothing rather than a partial block.
  const props = blockProps(part);
  if (props === undefined) return null;

  // `render_<kind>` → the matching primitive via the shared dispatcher (also
  // used for container children). The agent validated the args against the
  // catalog schema, so the props shape is sound; unknown kinds degrade to null.
  const kind = part.toolName.replace(/^render_/, '');
  return renderBlock(kind, props);
}
